"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  buildExecutiveSummary,
  DOC_KINDS,
  type DocKind,
  draftWords,
  type ExecutiveSummaryInput,
  type LengthUnit,
  lengthStatus,
  SHARE_CHOICES,
  summaryTarget,
} from "@/lib/tool-kit/business-templates/executive-summary";

/** English strings. A translated content file overrides these through `ui`. */
export const EXECUTIVE_SUMMARY_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  kindLabel: "What is the summary for?",
  kinds: {
    report: "A business report",
    plan: "A business plan",
    proposal: "A project proposal",
  },
  documentHeading: "The document",
  titleLabel: "Title of the full document",
  titlePlaceholder: "e.g. Reducing Food Waste in the Campus Cafeteria",
  lengthLabel: "Length of the full document",
  lengthPlaceholder: "e.g. 10",
  unitLabel: "Unit",
  units: { words: "Words", pages: "Pages (500 words each)" },
  shareLabel: "Share of the document",
  shares: { "5": "5%", "8": "8%", "10": "10%" },
  capLabel: "Never longer than one page (500 words)",
  lengthHint:
    "Summaries are usually 5–10% of the full document and rarely longer than a page. If your instructor sets a limit, use that.",
  answersHeading: "Your answers",
  purposeLabels: {
    report: "Purpose and background",
    plan: "The business — what it is and does",
    proposal: "Purpose of the proposal",
  },
  problemLabels: {
    report: "The problem or question",
    plan: "The opportunity — the customer problem",
    proposal: "The need or problem",
  },
  findingsLabels: {
    report: "Key findings — one per line",
    plan: "Why it will succeed — one point per line",
    proposal: "Approach and expected results — one per line",
  },
  recommendationLabels: {
    report: "Recommendation",
    plan: "The request — funding or decision sought",
    proposal: "The request",
  },
  nextLabels: {
    report: "Cost, benefits and next steps",
    plan: "Financial highlights and next steps",
    proposal: "Timeline and budget",
  },
  placeholders: {
    purpose: "Why this document exists and who it is for.",
    problem: "What is wrong or unknown, in a sentence or two.",
    findings: "e.g. Cafeteria waste fell 18% in the pilot",
    recommendation: "What you want the reader to do or decide.",
    next: "Costs, savings, deadlines, who acts first.",
  },
  resultsHeading: "Length check",
  statTarget: "Target length (words)",
  statDraft: "Your answers (words)",
  statTargetNone: "—",
  statusEmpty: "Type your answers above to compare them with the target.",
  statusUnder: "Under the target by {words}: there is room to add detail.",
  statusOn: "On target (within 10%).",
  statusOver: "Over the target by {words}: cut background and detail.",
  noTarget:
    "Enter the length of the full document to get a target length for the summary.",
  cappedNote: "Capped at one page. The share alone would give {words} words.",
  fileName: "executive-summary",
  document: {
    heading: "Your executive summary outline",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part has your content.",
  },
  defaultTitle: "Executive Summary",
  titleWithName: "Executive Summary: {name}",
  subtitle: {
    report: "Business report",
    plan: "Business plan",
    proposal: "Project proposal",
  },
  subtitleTarget: "target {words} words",
  parts: {
    report: {
      purpose: "Purpose and Background",
      problem: "The Problem",
      findings: "Key Findings",
      recommendation: "Recommendation",
      next: "Cost and Next Steps",
    },
    plan: {
      purpose: "The Business",
      problem: "The Opportunity",
      findings: "Why It Will Succeed",
      recommendation: "The Request",
      next: "Financial Highlights and Next Steps",
    },
    proposal: {
      purpose: "Purpose",
      problem: "The Need",
      findings: "Approach and Expected Results",
      recommendation: "The Request",
      next: "Timeline and Budget",
    },
  },
  tips: {
    report: {
      purpose:
        "Say what the report is for in a sentence. Skip history the reader already knows.",
      problem:
        "State the problem or question the report answers, not the background to it.",
      findings:
        "Give the three to five findings that lead to your recommendation, each with its number.",
      recommendation:
        "Say plainly what the reader should do. This is the part most readers act on.",
      next: "Give the cost, the expected benefit and the first step.",
    },
    plan: {
      purpose: "Say what the business is and does, and where it stands.",
      problem:
        "Name the customer and the problem, with one figure that shows it is real.",
      findings:
        "Give your advantage and your proof: sales, pilots, partners, experience.",
      recommendation: "Say how much you need or what you want decided.",
      next: "Give headline numbers and the next milestone.",
    },
    proposal: {
      purpose: "Say what you propose in a sentence.",
      problem: "Show that the need is real and who has it.",
      findings: "Summarize your approach and what it will achieve, measurably.",
      recommendation:
        "State what you are asking the reader to approve or fund.",
      next: "Give the timeline, the budget and what is delivered when.",
    },
  },
  prompts: {
    report: {
      purpose: "What is the report for, and who asked for it?",
      problem: "What problem or question does the report answer?",
      findings: "List the key findings, each with the number that supports it.",
      recommendation: "What should the reader do or decide?",
      next: "What will it cost, what will it save, and what happens first?",
    },
    plan: {
      purpose: "What is the business, in a sentence or two?",
      problem: "Who is the customer, and what problem do you solve?",
      findings: "Why will it succeed? List your advantage and your proof.",
      recommendation: "How much funding, or what decision, do you need?",
      next: "What are the headline numbers and the next milestone?",
    },
    proposal: {
      purpose: "What are you proposing?",
      problem: "What need or problem does it address, and for whom?",
      findings: "What is the approach, and what will it achieve?",
      recommendation: "What do you want the reader to approve or fund?",
      next: "What is the timeline and the budget?",
    },
  },
  target: "Target: {words} words ({share}).",
  targetShare: "About {share} of the summary.",
  checkHeading: "Before You Hand It In",
  checks: [
    "The summary was written last, from the finished document",
    "It makes sense to a reader who never opens the rest",
    "Every number in it also appears in the document, and the numbers agree",
    "It does not introduce anything that is not in the document",
    "It is within the length your instructor or client set",
  ],
  example: {
    title: "Reducing Food Waste in the Campus Cafeteria",
    kind: "report",
    purpose:
      "This report reviews a six-week pilot of three waste-reduction measures in the main campus cafeteria, for the Sustainability Committee.",
    problem:
      "The cafeteria throws away about a quarter of the food it prepares, which costs money and undermines the university's emissions target.",
    findings:
      "Smaller default portions cut plate waste by 18%\nDaily prep forecasts cut unsold food by 12%\nA discount on end-of-day meals recovered 9% of surplus",
    recommendation:
      "Adopt all three measures from January and review the results after one term.",
    next: "Costs: £2,400 for signage and training. Expected savings: £7,000 a year. First step: brief kitchen staff in December.",
    length: "10",
    unit: "pages",
    share: "5",
    cap: true,
  },
} as const;

const EMPTY: ExecutiveSummaryInput = {
  title: "",
  kind: "report",
  purpose: "",
  problem: "",
  findings: "",
  recommendation: "",
  next: "",
  length: "",
  unit: "words",
  share: "10",
  cap: true,
};

type TextKey =
  | "title"
  | "purpose"
  | "problem"
  | "findings"
  | "recommendation"
  | "next"
  | "length";

export default function ExecutiveSummaryGeneratorWidget() {
  const strings = useToolUi(EXECUTIVE_SUMMARY_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState<ExecutiveSummaryInput>(EMPTY);
  const doc = useMemo(
    () => buildExecutiveSummary(input, strings, fmt),
    [input, strings, fmt]
  );
  const target = summaryTarget(input);
  const words = draftWords(input);
  const status = lengthStatus(words, target?.words ?? null);
  const gap = target ? Math.abs(words - target.words) : 0;
  const statusText = {
    empty: strings.statusEmpty,
    under: t(strings.statusUnder, { words: fmt(gap) }),
    on: strings.statusOn,
    over: t(strings.statusOver, { words: fmt(gap) }),
  }[status];
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput({ ...EMPTY, ...strings.example })}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.documentHeading}>
        <PillGroup<DocKind>
          label={strings.kindLabel}
          name="es-kind"
          onChange={(kind) => setInput((previous) => ({ ...previous, kind }))}
          options={DOC_KINDS.map((value) => ({
            value,
            label: strings.kinds[value],
          }))}
          value={input.kind}
        />
        <TextField
          id="es-title"
          label={strings.titleLabel}
          onChange={update("title")}
          placeholder={strings.titlePlaceholder}
          value={input.title}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id="es-length"
            label={strings.lengthLabel}
            numeric
            onChange={update("length")}
            placeholder={strings.lengthPlaceholder}
            value={input.length}
          />
          <SelectField<LengthUnit>
            id="es-unit"
            label={strings.unitLabel}
            onChange={(unit) => setInput((previous) => ({ ...previous, unit }))}
            options={(["words", "pages"] as const).map((value) => ({
              value,
              label: strings.units[value],
            }))}
            value={input.unit}
          />
          <SelectField
            id="es-share"
            label={strings.shareLabel}
            onChange={(share) =>
              setInput((previous) => ({
                ...previous,
                share: share as ExecutiveSummaryInput["share"],
              }))
            }
            options={SHARE_CHOICES.map((value) => ({
              value,
              label: strings.shares[value],
            }))}
            value={input.share}
          />
        </div>
        <Checkbox
          checked={input.cap}
          label={strings.capLabel}
          onChange={(cap) => setInput((previous) => ({ ...previous, cap }))}
        />
        <p className="text-muted-foreground text-xs">{strings.lengthHint}</p>
      </Panel>

      <Panel title={strings.answersHeading}>
        <TextAreaField
          id="es-purpose"
          label={strings.purposeLabels[input.kind]}
          onChange={update("purpose")}
          placeholder={strings.placeholders.purpose}
          prose
          rows={3}
          value={input.purpose}
        />
        <TextAreaField
          id="es-problem"
          label={strings.problemLabels[input.kind]}
          onChange={update("problem")}
          placeholder={strings.placeholders.problem}
          prose
          rows={3}
          value={input.problem}
        />
        <TextAreaField
          id="es-findings"
          label={strings.findingsLabels[input.kind]}
          onChange={update("findings")}
          placeholder={strings.placeholders.findings}
          prose
          rows={4}
          value={input.findings}
        />
        <TextAreaField
          id="es-recommendation"
          label={strings.recommendationLabels[input.kind]}
          onChange={update("recommendation")}
          placeholder={strings.placeholders.recommendation}
          prose
          rows={3}
          value={input.recommendation}
        />
        <TextAreaField
          id="es-next"
          label={strings.nextLabels[input.kind]}
          onChange={update("next")}
          placeholder={strings.placeholders.next}
          prose
          rows={3}
          value={input.next}
        />
      </Panel>

      <section aria-live="polite" className="space-y-3">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.resultsHeading}
        </h2>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.statTarget}
            value={target ? fmt(target.words) : strings.statTargetNone}
          />
          <StatCard label={strings.statDraft} value={fmt(words)} />
        </StatGrid>
        <Notice>
          {target ? statusText : strings.noTarget}
          {target?.capped
            ? ` ${t(strings.cappedNote, { words: fmt(Math.round(target.raw)) })}`
            : ""}
        </Notice>
      </section>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="es"
        labels={strings.document}
      />
    </div>
  );
}
